import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWorkspaces } from '../hooks/useWorkspaces';
import { Spinner } from '../components/UI';
import { apiFetch } from '../services/api';
import {
  AppShell, TopNav, NavDrawer, BodyGrid, PageBody, useDrawer,
} from '../components/Layout';
import AddTaskDrawer from '../components/AddTaskDrawer';

const AGENT_COLORS = {
  ceo:'#0e0e0d', builder:'#378add', content:'#5dcaa5', marketing:'#c8563a',
  research:'#e8a87c', ops:'#c8563a', founder:'#0e0e0d',
};
const STATUS_META = {
  pending:      { label:'Pending',  color:'gray'  },
  in_progress:  { label:'Running',  color:'dark'  },
  needs_review: { label:'Review',   color:'amber' },
  done:         { label:'Done',     color:'green' },
  blocked:      { label:'Blocked',  color:'red'   },
};
const STATUS_COLORS = {
  gray:  { bg:'var(--paper-3)',          text:'var(--ink-mute)' },
  dark:  { bg:'var(--ink)',              text:'var(--paper)'    },
  green: { bg:'rgba(93,202,165,0.15)',   text:'#2d8a6e'         },
  amber: { bg:'rgba(232,168,124,0.2)',   text:'#9a6030'         },
  red:   { bg:'rgba(224,112,112,0.15)',  text:'#c03030'         },
};

// Normalize a query param value to lowercase, defaulting to 'all'
function normalizeParam(value) {
  if (!value || value.toLowerCase() === 'all') return 'all';
  return value.toLowerCase();
}

function timeAgo(ts) {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return new Date(ts).toLocaleDateString();
}

export default function TasksPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeWorkspace, loading: wsLoading } = useWorkspaces();
  const drawer = useDrawer();

  const [tasks, setTasks]               = useState([]);
  const [taskLoading, setTaskLoading]   = useState(false);
  const [addOpen, setAddOpen]           = useState(false);

  // Initialize filters from URL query params (case-insensitive)
  const searchParams = new URLSearchParams(location.search);
  const [filterStatus, setFilterStatus] = useState(() => normalizeParam(searchParams.get('status')));
  const [filterAgent, setFilterAgent]   = useState(() => normalizeParam(searchParams.get('agent')));

  // Sync filter state back to URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (filterStatus !== 'all') params.set('status', filterStatus);
    else params.delete('status');
    if (filterAgent !== 'all') params.set('agent', filterAgent);
    else params.delete('agent');
    const newSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, '');
    if (newSearch !== currentSearch) {
      navigate({ search: newSearch ? `?${newSearch}` : '' }, { replace: true });
    }
  }, [filterStatus, filterAgent]);

  useEffect(() => {
    if (!activeWorkspace?.id) return;
    setTaskLoading(true);
    apiFetch(`/api/workspaces/${activeWorkspace.id}/tasks`)
      .then(data => setTasks(Array.isArray(data) ? data : []))
      .catch(() => setTasks([]))
      .finally(() => setTaskLoading(false));
  }, [activeWorkspace?.id]);

  const handleTaskSaved = (newTask) => {
    setTasks(prev => [newTask, ...prev]);
  };

  const filtered = tasks.filter(t => {
    // Case-insensitive status comparison
    if (filterStatus !== 'all' && t.status?.toLowerCase() !== filterStatus) return false;
    // Case-insensitive agent comparison
    if (filterAgent !== 'all' && t.agent_type?.toLowerCase() !== filterAgent) return false;
    return true;
  }).sort((a, b) => {
    if (a.status === 'in_progress' && b.status !== 'in_progress') return -1;
    if (b.status === 'in_progress' && a.status !== 'in_progress') return 1;
    if (a.priority !== b.priority) return a.priority - b.priority;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <AppShell>
      <TopNav
        breadcrumb="Tasks"
        drawerOpen={drawer.open}
        onToggleDrawer={drawer.toggle}
        drawerPinned={drawer.pinned}
      />
      <NavDrawer
        open={drawer.open}
        onClose={drawer.close}
        pinned={drawer.pinned}
        onTogglePin={drawer.togglePin}
      />

      <BodyGrid drawerPinned={drawer.pinned}>
        <PageBody>

          {/* ── Page header ── */}
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            marginBottom:'16px', flexWrap:'wrap', gap:'8px',
          }}>
            <div>
              <h1 style={{ fontFamily:'var(--serif)', fontSize:'26px', fontWeight:'400', marginBottom:'4px' }}>Tasks</h1>
              <p style={{ fontSize:'13px', color:'var(--ink-mute)' }}>
                {filtered.length} task{filtered.length !== 1 ? 's' : ''}
                {(filterStatus !== 'all' || filterAgent !== 'all') ? ' (filtered)' : ''}
              </p>
            </div>

            <button
              onClick={() => setAddOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px', borderRadius: 'var(--r)',
                border: 'none', background: 'var(--ink)', color: 'var(--paper)',
                fontSize: '13px', fontFamily: 'var(--sans)', fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize:'16px', lineHeight:1 }}>+</span>
              Add Task
            </button>
          </div>

          {/* ── Filter bar ── */}
          <div style={{
            display:'flex', alignItems:'center', gap:'16px', flexWrap:'wrap',
            marginBottom:'20px', padding:'12px 14px',
            background:'var(--white)', border:'1px solid var(--paper-3)',
            borderRadius:'var(--r)',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
              <span style={filterGroupLabel}>Status</span>
              {['all','pending','in_progress','done','blocked'].map(s => (
                <FilterChip key={s} active={filterStatus === s} onClick={() => setFilterStatus(s)}>
                  {s === 'all' ? 'All' : STATUS_META[s]?.label || s}
                  {s !== 'all' && (
                    <span style={{ marginLeft:'4px', opacity:0.6 }}>
                      {tasks.filter(t => t.status?.toLowerCase() === s).length}
                    </span>
                  )}
                </FilterChip>
              ))}
            </div>

            <div style={{ width:'1px', height:'20px', background:'var(--paper-3)', flexShrink:0 }} />

            <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
              <span style={filterGroupLabel}>Agent</span>
              {['all','ceo','builder','content','marketing','research','ops','founder'].map(a => (
                <FilterChip
                  key={a}
                  active={filterAgent === a}
                  onClick={() => setFilterAgent(a)}
                  dot={a !== 'all' ? AGENT_COLORS[a] : null}
                >
                  {a === 'all' ? 'All' : a.charAt(0).toUpperCase() + a.slice(1)}
                </FilterChip>
              ))}
            </div>
          </div>

          {/* ── Task list ── */}
          {taskLoading || wsLoading ? (
            <div style={{ display:'flex', justifyContent:'center', paddingTop:'48px' }}>
              <Spinner size={28} />
            </div>
          ) : filtered.length === 0 ? (
            <div style={{
              textAlign:'center', padding:'48px 24px',
              border:'1px dashed var(--paper-3)', borderRadius:'var(--r)',
            }}>
              <p style={{ fontSize:'14px', color:'var(--ink-mute)' }}>
                {tasks.length === 0
                  ? 'No tasks yet — add one above or let your CEO agent create them.'
                  : 'No tasks match the current filters.'}
              </p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {filtered.map(task => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onClick={() => navigate(`/dashboard/tasks/${task.id}?workspace_id=${activeWorkspace?.id}`)}
                />
              ))}
            </div>
          )}
        </PageBody>
      </BodyGrid>

      <AddTaskDrawer
        open={addOpen}
        workspaceId={activeWorkspace?.id}
        onClose={() => setAddOpen(false)}
        onSaved={handleTaskSaved}
      />
    </AppShell>
  );
}

// --- TaskRow, FilterChip, filterGroupLabel unchanged below ---

function TaskRow({ task, onClick }) {
  const sm    = STATUS_META[task.status] || { label: task.status, color:'gray' };
  const c     = STATUS_COLORS[sm.color] || STATUS_COLORS.gray;
  const color = AGENT_COLORS[task.agent_type] || '#888';
  const isHigh = task.priority <= 2;

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--white)',
        border: `1px solid ${isHigh ? 'rgba(200,86,58,0.25)' : 'var(--paper-3)'}`,
        borderRadius: 'var(--r)', padding: '14px 18px', cursor: 'pointer',
        display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'start',
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'5px', flexWrap:'wrap' }}>
          {isHigh && (
            <span style={{
              fontSize:'10px', fontWeight:'600', color:'var(--accent)',
              background:'rgba(200,86,58,0.08)', padding:'2px 6px', borderRadius:'4px',
            }}>P{task.priority}</span>
          )}
          <span style={{ fontSize:'14px', fontWeight:'500' }}>{task.title}</span>
        </div>
        {task.description && (
          <p style={{ fontSize:'12px', color:'var(--ink-soft)', lineHeight:1.5, marginBottom:'8px' }}>
            {task.description.slice(0, 140)}{task.description.length > 140 ? '…' : ''}
          </p>
        )}
        <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
          <span style={{
            display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'11px',
            fontWeight:'500', padding:'2px 8px', borderRadius:'10px',
            background:`${color}18`, color,
          }}>
            <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:color }} />
            {task.agent_type}
          </span>
          <span style={{ fontSize:'11px', color:'var(--ink-mute)' }}>{timeAgo(task.created_at)}</span>
          {task.error_message && <span style={{ fontSize:'11px', color:'#e07070' }}>⚠ Error</span>}
        </div>
      </div>
      <span style={{
        fontSize:'11px', fontWeight:'500', padding:'3px 9px', borderRadius:'10px',
        background: c.bg, color: c.text, whiteSpace:'nowrap', marginTop:'2px',
      }}>
        {sm.label}
      </span>
    </div>
  );
}

const filterGroupLabel = {
  fontSize: '11px', fontWeight: '600', letterSpacing: '0.06em',
  textTransform: 'uppercase', color: 'var(--ink-mute)', whiteSpace: 'nowrap', flexShrink: 0,
};

function FilterChip({ active, onClick, dot, children }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '4px 10px', borderRadius: '100px', fontSize: '12px',
      fontFamily: 'var(--sans)', cursor: 'pointer', whiteSpace: 'nowrap',
      border: `1px solid ${active ? 'var(--ink)' : 'var(--paper-3)'}`,
      background: active ? 'var(--ink)' : 'var(--white)',
      color: active ? 'var(--paper)' : 'var(--ink)',
      fontWeight: active ? '500' : '400', transition: 'all 0.12s',
    }}>
      {dot && <span style={{ width:'6px', height:'6px', borderRadius:'50%', background: active ? 'var(--paper)' : dot, flexShrink:0 }} />}
      {children}
    </button>
  );
}